/**
 * This file contains a custom React hook for managing drawers in a URL.
 * Drawers are handled by an array in the URL. This allows us to have multiple drawers open at once.
 * The param is called 'drawers' and is an array of strings.
 */

import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import useCurrentUser from "~/hooks/user/useCurrentUser";
import { parseParams, stringifyParams } from "~/util/queryString";

/**
 * @interface DrawerExternal
 * @description The structure used externally when setting a drawer. i.e. What is seen in the URL.
 * @property {string} n - The name of the drawer
 * @property {any} d - The data associated with the drawer
 */
interface DrawerExternal {
  n: string;
  d: any;
}

/**
 * @type DrawerInternal
 * @description The structure used when setting a drawer as a developer. i.e. when calling a drawer in code.
 * @property {string} name - The name of the drawer
 * @property {any} data - The data associated with the drawer
 */
interface DrawerInternal {
  name?: string;
  data?: any;
}

// Global state for the query and drawer state
let globalQuery: any = {};
let globalDrawerState: DrawerExternal[] = [];

/**
 * @function findDrawer
 * @description Helper function to find a drawer in the array of drawers
 * @param {Drawer[]} drawers - The array of drawers
 * @param {string | Drawer} drawer - The drawer to find
 * @returns {Drawer | undefined} - The found drawer or undefined if not found
 */
const findDrawer = (
  drawers: DrawerExternal[],
  drawer: string | DrawerInternal
): DrawerExternal | undefined => {
  if (typeof drawer === "string") {
    return drawers?.find((d) => {
      if (typeof d === "string") {
        return d === drawer;
      }

      return d?.n === drawer;
    });
  }

  return drawers?.find((d) => {
    if (typeof d === "string") {
      return d === drawer?.name;
    }

    return d?.n === drawer?.name;
  });
};

/**
 * @function useDrawers
 * @description Custom hook for managing drawers in the URL
 * @param {string} preselectedName - The name of the preselected drawer
 * @returns {Object} - An object containing functions for managing drawers
 */
const useDrawers = (preselectedName?: string) => {
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const [searchParams] = useSearchParams();
  const {
    data: { id: userId },
    isLoading: userLoading,
  } = useCurrentUser();

  const queryString = searchParams.toString();

  const query = parseParams(queryString);
  globalQuery = query;

  const drawers: DrawerExternal[] = useMemo(() => query?.d || [], [query]);
  globalDrawerState = drawers;

  /**
   * @function openDrawer
   *
   * @description Opens a drawer
   * @param {string} drawer - The drawer to open
   */
  const openDrawer = (
    drawer: string | DrawerInternal = preselectedName || ""
  ) => {
    // If the drawer is already in the array, do nothing.
    if (findDrawer(drawers, drawer)) return;

    let compressedData: string | DrawerExternal = "";

    // If string, nothing to compress.
    if (typeof drawer === "string") {
      compressedData = drawer;
    } else {
      // Compress the drawer data to save space in the URL.
      compressedData = {
        n: preselectedName || drawer?.name || "",
        d: drawer?.data,
      };
    }

    const newQuery = { ...globalQuery, d: [...drawers, compressedData] };

    if (typeof compressedData === "string") {
      // handle the case when compressedData is a string
      // you might want to convert it to a DrawerExternal object
      const drawerExternal: DrawerExternal = { n: compressedData, d: {} };
      globalDrawerState = [...drawers, drawerExternal];
    } else {
      // compressedData is of type DrawerExternal
      globalDrawerState = [...drawers, compressedData];
    }
    globalQuery = newQuery;

    // Update the URL. merge the new drawer with the existing query
    navigate(pathname + stringifyParams(newQuery));
  };

  /**
   * @function closeDrawer
   * @description Closes a drawer
   * @param {string | Drawer} drawer - The drawer to close
   */
  const closeDrawer = (
    drawer: string | DrawerInternal = preselectedName || ""
  ) => {
    // If the drawer is not in the array, do nothing.
    if (!findDrawer(drawers, drawer)) return;

    const mutatedDrawers = [...globalDrawerState];

    // Remove the drawer from the array.
    const index = mutatedDrawers.findIndex((d) => {
      if (typeof d === "string") {
        return d === drawer;
      }

      return d?.n === drawer;
    });

    mutatedDrawers.splice(index, 1);

    // If the array is empty, remove the drawers param from the URL.
    if (mutatedDrawers.length === 0) {
      const newQuery = { ...globalQuery };
      delete newQuery.d;
      globalDrawerState = [];
      globalQuery = newQuery;
      navigate(pathname + stringifyParams(newQuery));
    }

    if (mutatedDrawers.length > 0) {
      // Update the URL.
      const newQuery = { ...globalQuery, d: mutatedDrawers };

      globalDrawerState = mutatedDrawers;
      globalQuery = newQuery;

      // Update the URL. merge the new drawer with the existing query
      navigate(pathname + stringifyParams(newQuery));
    }
  };

  /**
   * @function isDrawerOpen
   * @description Checks if a drawer is open
   * @param {string | Drawer} drawer - The drawer to check
   * @returns {boolean} - True if the drawer is open, false otherwise
   */
  const isDrawerOpen = (
    drawer: string | DrawerInternal = preselectedName || ""
  ): boolean => {
    // If the user is not loaded or there is no user id, return false.
    if (userLoading || !userId) return false;

    return Boolean(findDrawer(drawers, drawer));
  };

  /**
   * @function areAnyDrawersOpen
   * @description Checks if any drawers are open
   * @returns {boolean} - True if any drawers are open, false otherwise
   */
  const areAnyDrawersOpen = (): boolean => {
    return drawers.length > 0;
  };

  /**
   * @function getDrawerData
   * @description Gets the data of a drawer
   * @param {string | Drawer} drawer - The drawer to get the data from
   * @returns {any} - The data of the drawer
   */
  const getDrawerData = useCallback(
    (drawer: string | DrawerInternal = preselectedName || ""): any => {
      const drawerData = findDrawer(drawers, drawer);

      return drawerData?.d || {};
    },
    [drawers, preselectedName]
  );

  return {
    getDrawerData,
    openDrawer,
    closeDrawer,
    isDrawerOpen,
    areAnyDrawersOpen,
  };
};

export default useDrawers;
